<script lang="ts">
  // Svelte / Kit
  import { tick } from 'svelte';
  import { beforeNavigate } from '$app/navigation';
  import { env } from '$env/dynamic/public';
  import type { BeforeNavigate } from '@sveltejs/kit';
  import { flip } from 'svelte/animate';
  // UI Component
  import { Button } from '$lib/components/ui/button';
  import * as DropdownMenu from '$lib/components/ui/dropdown-menu';
  import { Input } from '$lib/components/ui/input/index.js';
  import * as Select from '$lib/components/ui/select/index.js';
  import * as Table from '$lib/components/ui/table';
  // RoastLog
  import ConfirmationAlert from '$lib/components/ConfirmationAlert.svelte';
  import ControlButtonsContainer from '$lib/components/ControlButtonsContainer.svelte';
  import ContextDropdown from '$lib/components/queue/ContextDropdown.svelte';
  import EditRecipeDialog from '$lib/components/queue/EditRecipeDialog.svelte';
  import { fetchStarredRoast } from '$lib/dataLoader';
  import logger from '$lib/logger';
  import {
    roastables,
    roastQueue,
    roasts,
    starredRoasts,
    session,
    userProfile,
    selectedRoaster
  } from '$lib/PersistedState.svelte';
  import { sortByLabel } from '$lib/RLogUtils';
  import { stopRecording } from '$lib/roasting/roastRecorder.svelte';
  import { globalState } from '$lib/State.svelte';
  import {
    RoastQueueStatus,
    type RoastQueue,
    type RoastQueueItem,
    type SelectChoice
  } from '$lib/types/RoastLog';
  // Others
  import EditRoastDialog from '$lib/components/queue/EditRoastDialog.svelte';
  import { Copy, Ellipsis, ListRestart, Menu, Scale, Trash2 } from '@lucide/svelte';
  import { dragHandle, dragHandleZone } from 'svelte-dnd-action';
  import { toast } from 'svelte-sonner';
  import { v4 as uuidv4 } from 'uuid';

  import {
    createRoast,
    CreateRoastApiError,
    getFailedUploads,
    removeFailedUpload
  } from '$lib/roasting/roastUploadService';
  import { onMount } from 'svelte';

  import { buildHeaders, roastUrls, tokenRefresh } from '$lib/api';
  import type { NewRoast } from '$lib/types/RoastLog';

  let reUploadDialogOpen = $state(false);
  let deleteItemDialogOpen = $state(false);
  let clearQueueDialogOpen = $state(false);
  let resetItemDialogOpen = $state(false);
  let editRecipeDialogOpen = $state(false);
  let qItemToEditIndex: number | null = $state(null);
  let updateRoastDialogOpen = $state(false);
  let idToUpdateRoast: string | null = $state(null);
  let numberOfFailedRoasts: number = $state(0);

  let idToDelete: string | null = null;
  let idToReset: string | null = null;
  // svelte-ignore non_reactive_update
  let tableElement: HTMLElement | null = null;

  let failedUploads: { id: string; data: { newRoastData: NewRoast } }[] = [];

  const ROASTLOG_HOST = env.PUBLIC_ROASTLOG_HOST || 'https://roastlog.com';

  let roastableChoices: SelectChoice[] = $derived(
    roastables.current.map(
      (r): SelectChoice => ({
        value: r.id.toString(),
        label: r.name.trim()
      })
    )
  );

  let referenceProfileChoices: SelectChoice[] = $derived(
    sortByLabel(
      roasts.current.map(
        (r): SelectChoice => ({
          value: r.roast_id.toString(),
          label: `${r.name.trim()} - #${r.roast_id}`,
          name: r.name.trim()
        })
      )
    )
  );

  $effect(() => {
    if (editRecipeDialogOpen === false) {
      qItemToEditIndex = null;
    }
  });

  $effect(() => {
    if (updateRoastDialogOpen === false) {
      idToUpdateRoast = null;
    }
  });

  onMount(async () => {
    failedUploads = await getFailedUploads();

    if (failedUploads.length > 0) {
      logger.warn(`Found ${failedUploads.length} failed uploads.`);
      numberOfFailedRoasts = failedUploads.length;
    }

    if (session.daysToExpire < 7) {
      logger.info('Requesting new login token', { daysToExpire: session.daysToExpire });
      const resp = await tokenRefresh(session.token);
      if (resp.status !== 200) {
        let errString = resp.response.non_field_errors?.join('. ');
        if (!errString) {
          errString = resp.response.error;
        }
        logger.error(`Error fetching new login token. ${errString}`);
        // toast.error(`Error fetching new login token. ${errString}`);
      } else {
        if (resp.response.token) {
          logger.info('Saving refresh token.');
          session.token = resp.response.token;
        } else {
          logger.error('Did not receive correct refresh token.', {
            response: resp.response
          });
        }
      }
    }
  });

  beforeNavigate((nav: BeforeNavigate) => {
    const validQueue = validateRoastQueue();
    if (!validQueue) {
      nav.cancel();
      toast.error('Please select a roastable for each item in the queue.');
    }
  });

  const sortQueueByStatus = (qItems: RoastQueue): RoastQueue => {
    const sortableItems: RoastQueue = [];
    const unsortableItems: RoastQueue = [];

    // Simply put the completed or in-progress items at the front of the queue, and anything that is
    // "Queued" can go later, meaning its actually sortable with it's own group. When trying to sort
    // a queued item in front of an uploaded roast, it won't work and will just become the first
    // element in the unroasted section.
    qItems.forEach((qItem) => {
      if (qItem.status === RoastQueueStatus.Queued) {
        sortableItems.push(qItem);
      } else {
        unsortableItems.push(qItem);
      }
    });

    return unsortableItems.concat(sortableItems);
  };

  const handeReUploadFailedRoasts = async () => {
    reUploadDialogOpen = false;
    let item;

    failedUploads.forEach(async (u) => {
      try {
        const resp = await createRoast({
          url: roastUrls.newRoast,
          headers: buildHeaders(),
          newRoastData: u.data.newRoastData
        });
        logger.info('Successfully uploaded previoulsy failed roast.', { roastId: resp.roast_id });

        roastQueue.setStatusById(u.id, RoastQueueStatus.Uploaded);
        removeFailedUpload(u.id);
        logger.info('removed failed upload', { id: u.id });

        failedUploads = await getFailedUploads();
        numberOfFailedRoasts = failedUploads.length;
        await new Promise((resolve) => setTimeout(resolve, 1000));
      } catch (error) {
        if (error instanceof CreateRoastApiError) {
          logger.error(
            `Upload failed: ${error.status} ${error.message} for roast ID: ${error.newRoastId}`
          );
          toast.error(`Failed to upload roast: ${error.message}`);
        } else {
          logger.error('Unexpected error during upload:', { error });
          toast.error('An unexpected error occurred during upload');
        }
      }
    });
  };

  const deleteAllFailedUploads = async () => {
    logger.info('Deleting all failed roasts');
    reUploadDialogOpen = false;

    let deleteCount = 0;
    let item;

    failedUploads.forEach((u) => {
      item = roastQueue.findItemById(u.id);

      logger.info('Deleting failed roast', { id: u.id, inQueue: item !== undefined });

      removeFailedUpload(u.id);
      if (item) {
        roastQueue.deleteItemById(u.id);
      }
      deleteCount++;
    });

    numberOfFailedRoasts = failedUploads.length;
    logger.info(`Succcessfully deleted ${deleteCount} failed roasts.`);
  };

  const handleAddRoastable = () => {
    const newRoastQueueItem: RoastQueueItem = {
      id: uuidv4(),
      status: RoastQueueStatus.Queued,
      endingMassDisabled: true,
      uploaded: false
    };
    roastQueue.append(newRoastQueueItem);
    scrollToBottom();
  };

  const handleDeleteQueueItem = (): void => {
    if (idToDelete) {
      roastQueue.deleteItemById(idToDelete);
      idToDelete = null;
    }
    deleteItemDialogOpen = false;
  };

  const handleResetQueueItem = (): void => {
    if (idToReset) {
      const qIndex = roastQueue.getItemIndexById(idToReset);
      if (qIndex !== null) {
        roastQueue.updateItemAt(qIndex, { status: RoastQueueStatus.Queued });
        roastQueue.items = sortQueueByStatus(roastQueue.items);
      }
      idToReset = null;
    }
    resetItemDialogOpen = false;
  };

  const handleConsider = (e: { detail: { items: RoastQueue } }) => {
    roastQueue.items = sortQueueByStatus(e.detail.items);
  };

  const handleFinalize = (e: { detail: { items: RoastQueue } }) => {
    roastQueue.items = e.detail.items;
  };

  const handleRoastableChanged = (i: number, roastableId: string) => {
    const rId = parseInt(roastableId);

    const selectedRoastable = roastables.current.find((r) => r.id === rId);
    if (!selectedRoastable) {
      logger.error(`Could not find roastable ${roastableId}`);
      return;
    }

    roastQueue.updateItemAt(i, {
      roastable: selectedRoastable
    });

    const matchingTemplate: SelectChoice | undefined = referenceProfileChoices.find((p) => {
      return p.name == selectedRoastable.name.trim();
    });

    if (matchingTemplate) {
      handleTemplateChanged(i, matchingTemplate.value);
    }
  };

  const handleTemplateChanged = (i: number, referenceRoastProfileId: string) => {
    const rId = parseInt(referenceRoastProfileId);
    const selectedRoastProfile = roasts.current.find((r) => r.roast_id === rId);

    if (!selectedRoastProfile) {
      logger.error(`Could not find reference roast profile ${referenceRoastProfileId}`);
      return;
    }

    // Now that the template has been changed, check whether the roasting  profile should be
    // downloaded. Note, this is an async operation and we don't await here.
    if (starredRoasts.current[rId] === undefined) {
      fetchStarredRoast(rId);
    }

    roastQueue.updateItemAt(i, {
      referenceRoastId: referenceRoastProfileId,
      referenceRoastableName: selectedRoastProfile.name,
      notes: selectedRoastProfile.notes
    });
  };

  const canBeReset = (qItem: RoastQueueItem): boolean => {
    if (!globalState.isRecording && qItem.status === RoastQueueStatus.InProgress) {
      return true;
    }
    return qItem.status === RoastQueueStatus.Saving;
  };

  const canEditRecipe = (qItem: RoastQueueItem): boolean => {
    return (
      qItem.roastable !== undefined &&
      (qItem.status === RoastQueueStatus.InProgress || qItem.status === RoastQueueStatus.Queued)
    );
  };

  const validateRoastQueue = (): boolean =>
    roastQueue.items.every((item) => Boolean(item.roastable?.id));

  const getQItemClasses = (qIndex: number): string => {
    const qItem = roastQueue.items[qIndex];

    if (!qItem.roastable?.id) {
      return 'border border-destructive';
    }
    return '';
  };

  const scrollToBottom = async () => {
    await tick();
    if (tableElement) {
      tableElement.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }
  };
</script>

<ControlButtonsContainer>
  <Button onclick={() => (clearQueueDialogOpen = true)} disabled={roastQueue.length === 0}
    >Clear Queue</Button
  >
  <Button onclick={handleAddRoastable}>Add Roastable</Button>
  {#snippet right()}
    {#if numberOfFailedRoasts > 0}
      <Button
        variant="link"
        onclick={() => {
          reUploadDialogOpen = true;
        }}
      >
        Re-upload {numberOfFailedRoasts} failed roasts
      </Button>
    {/if}
  {/snippet}
</ControlButtonsContainer>

{#if qItemToEditIndex !== null}
  <EditRecipeDialog bind:open={editRecipeDialogOpen} qItemIndex={qItemToEditIndex} />
{/if}

{#if idToUpdateRoast !== null}
  <EditRoastDialog bind:open={updateRoastDialogOpen} qItemId={idToUpdateRoast} />
{/if}

<!-- Re-upload failed roasts dialog -->
<ConfirmationAlert
  title="Attempt to upload failed roasts"
  description=""
  confirmMsg="Try again"
  handleOk={handeReUploadFailedRoasts}
  bind:open={reUploadDialogOpen}
>
  {#snippet richDescription()}
    <div class="flex flex-col gap-5">
      <div class="text-primary text-lg font-bold">Please read before proceeding!</div>
      <div>
        You currently have failed roasts that have have not been uploaded after multiple attempts.
        This can happen due to an expired login session, or network errors.
        <span class="bold underline">
          These roasts are saved to your computer, and are not lost.
        </span>
      </div>
      <div>
        Once you're done with your roasting session, we recommend logging out, logging back in, and
        then reattempting this action if you would like to recover these roasts. You can optionally
        delete them all.
      </div>
      <div>
        Your options now are:
        <ol class="list-decimal p-5">
          <li>Cancel - Close this window, and do nothing</li>
          <li>
            Delete all - Delete all failed roasts. <span class="bold underline"
              >This is irrerversable!</span
            >
          </li>
          <li>Try again - Attempt to re-upload roasts</li>
        </ol>
      </div>
    </div>
  {/snippet}

  {#snippet action2()}
    <Button variant="destructive" class="ml-2" onclick={deleteAllFailedUploads}
      >Delete all
      <Trash2 />
    </Button>
  {/snippet}
</ConfirmationAlert>

<!-- Delete a row dialog -->
<ConfirmationAlert
  title="Delete item?"
  description="Are you sure you want to remove this item from the roast queue?"
  confirmMsg="Delete"
  handleOk={handleDeleteQueueItem}
  bind:open={deleteItemDialogOpen}
></ConfirmationAlert>

<!-- Reset an item dialog -->
<ConfirmationAlert
  title="Reset item?"
  description="Are you sure you want to reset this item's state? Restting this item will move it from it's current state back to Queued so that you can roast it."
  confirmMsg="Reset"
  handleOk={handleResetQueueItem}
  bind:open={resetItemDialogOpen}
></ConfirmationAlert>

<!-- Clear the entire queue dialog -->
<ConfirmationAlert
  title="Clear queue?"
  description="Are you sure you want to clear all of the items from the roast queue?"
  confirmMsg="Clear"
  handleOk={() => {
    roastQueue.reset();
    clearQueueDialogOpen = false;
    if (globalState.isRecording) {
      stopRecording();
    }
  }}
  bind:open={clearQueueDialogOpen}
></ConfirmationAlert>

<div class="bg-card justify-normal rounded-lg p-5 pt-2">
  <Table.Root bind:ref={tableElement} class="mt-2 w-full">
    <Table.Header>
      <Table.Row>
        <Table.Head></Table.Head>
        <Table.Head class="px-4 py-3">Queue item</Table.Head>
        <Table.Head class="px-4 py-3">Roastable</Table.Head>
        <Table.Head class="px-4 py-3">Reference profile</Table.Head>
        <Table.Head class="px-4 py-3">Start mass ({selectedRoaster.massScale})</Table.Head>
        <Table.Head class="px-4 py-3">Status</Table.Head>
        <Table.Head class="px-4 py-3">Actions</Table.Head>
      </Table.Row>
    </Table.Header>

    <tbody
      use:dragHandleZone={{ items: roastQueue.items, flipDurationMs: 300, dropTargetStyle: {} }}
      onconsider={handleConsider}
      onfinalize={handleFinalize}
    >
      {#each roastQueue.items as queueItem, i (queueItem.id)}
        <tr
          class="bg-card border-b last:border-b-0 dark:border-gray-700"
          animate:flip={{ duration: 300 }}
        >
          <td class="handle px-4 py-3">
            <div use:dragHandle aria-label="drag-handle for {queueItem.id}" class="handle">
              <Menu />
            </div>
          </td>
          <!-- counter -->
          <td class="w-[120px] px-4 py-3">
            {i + 1} of {roastQueue.length}
          </td>

          <!-- roastable -->
          <td class="px-4 py-3">
            <Select.Root
              type="single"
              onValueChange={(roastableId) => handleRoastableChanged(i, roastableId)}
              disabled={queueItem.status !== RoastQueueStatus.Queued &&
                queueItem.status !== RoastQueueStatus.InProgress}
              value={queueItem.roastable?.id ? queueItem.roastable.id.toString() : queueItem.id}
            >
              <Select.Trigger class={getQItemClasses(i)}>{queueItem.roastable?.name}</Select.Trigger
              >
              <Select.Content>
                {#each roastableChoices as roastable}
                  <Select.Item value={roastable.value}>
                    {roastable.label}
                  </Select.Item>
                {/each}
              </Select.Content>
            </Select.Root>
          </td>

          <!-- template -->
          <td class="px-4 py-3">
            <Select.Root
              type="single"
              onValueChange={(referenceRoastProfileId: string) =>
                handleTemplateChanged(i, referenceRoastProfileId)}
              disabled={queueItem.status !== RoastQueueStatus.Queued &&
                queueItem.status !== RoastQueueStatus.InProgress}
              bind:value={queueItem.referenceRoastId}
            >
              <Select.Trigger>{queueItem.referenceRoastableName}</Select.Trigger>
              <Select.Content>
                {#each referenceProfileChoices as referenceProfile}
                  <Select.Item value={referenceProfile.value}>
                    {referenceProfile.label}
                  </Select.Item>
                {/each}
              </Select.Content>
            </Select.Root>
          </td>

          <!-- reference -->
          <td class="w-[100px] px-4 py-3">
            <Input
              type="number"
              min="0"
              bind:value={queueItem.startingMass}
              disabled={queueItem.status !== RoastQueueStatus.Queued}
            />
          </td>

          <!-- Status  -->
          <td class="px-4 py-3">
            {#if queueItem.roastId}
              {queueItem.status}:
              <div class="text-sm">
                <a
                  href="{ROASTLOG_HOST}/roasts/{queueItem.roastId}"
                  target="_blank"
                  class="text-primary hover:text-secondary underline hover:no-underline"
                  >#{queueItem.roastId}</a
                >
              </div>
            {:else}
              <span
                class={queueItem.status === RoastQueueStatus.Failed
                  ? 'text-rose-600 dark:text-red-400'
                  : ''}
              >
                {queueItem.status}
              </span>
            {/if}
          </td>

          <!-- Actions  -->
          <td class="flex items-center px-4 py-3">
            <span title="Duplicate">
              <Copy
                class="mr-2 h-4 w-4 cursor-pointer"
                onclick={() => {
                  roastQueue.duplicateItemById(queueItem.id);
                  scrollToBottom();
                }}
              />
            </span>

            <DropdownMenu.Root>
              <DropdownMenu.Trigger>
                <Ellipsis />
              </DropdownMenu.Trigger>
              <DropdownMenu.Content>
                <DropdownMenu.Group>
                  <DropdownMenu.Item
                    class="hover:cursor-pointer"
                    onclick={() => {
                      idToDelete = queueItem.id;
                      deleteItemDialogOpen = true;
                    }}
                  >
                    <Trash2 class="mr-2 h-4 w-4 " /> Delete
                  </DropdownMenu.Item>

                  {#if queueItem.roastId && queueItem.startingMass}
                    <DropdownMenu.Item
                      class="hover:cursor-pointer"
                      onclick={() => {
                        idToUpdateRoast = queueItem.id;
                        updateRoastDialogOpen = true;
                      }}
                    >
                      <Scale class="mr-2 h-4 w-4" /> Update roast
                    </DropdownMenu.Item>
                  {/if}

                  {#if canBeReset(queueItem)}
                    <DropdownMenu.Item
                      class="hover:cursor-pointer"
                      onclick={() => {
                        idToReset = queueItem.id;
                        resetItemDialogOpen = true;
                      }}
                    >
                      <ListRestart class="mr-2 h-4 w-4" /> Reset
                    </DropdownMenu.Item>
                  {/if}

                  {#if canEditRecipe(queueItem) && queueItem.roastable}
                    <ContextDropdown
                      index={i}
                      components={queueItem.roastable.components}
                      startingMass={queueItem.startingMass}
                      bind:qItemToEditIndex
                      bind:dialogOpen={editRecipeDialogOpen}
                    />
                  {/if}
                </DropdownMenu.Group>
              </DropdownMenu.Content>
            </DropdownMenu.Root>
          </td>
        </tr>
      {/each}
    </tbody>
  </Table.Root>
</div>
